import { parseBackendDate } from '@modules/atendimento/utils/message-formatter'

// === Comunicação (chat) ===
// Clone das mutations do store do atendimento. Mutations não referenciam
// namespace — lógica idêntica, sem repontamento necessário.
export const SET_CONVERSAS = (state, payload) => {
  state.conversas = payload
}

// Sort cronológico por data_mensagem. Optimisticas ficam SEMPRE no fim
// até o REPLACE — clock skew client/server pode bagunçar a ordem com
// mensagens entrantes via SSE/polling.
function sortByDataMensagem(list) {
  return list.sort(function (a, b) {
    if (a._optimistic && !b._optimistic) return 1
    if (!a._optimistic && b._optimistic) return -1
    // Reusa o parser canônico do backend (atendimento/message-formatter),
    // adaptando Date → ms (0 quando ausente/inválida) para a comparação.
    const pa = parseBackendDate(a.data_mensagem)
    const pb = parseBackendDate(b.data_mensagem)
    const da = pa ? pa.getTime() : 0
    const db = pb ? pb.getTime() : 0
    if (da !== db) return da - db
    return (a.id || 0) - (b.id || 0)
  })
}

export const SET_MENSAGENS = (state, payload) => {
  if (!Array.isArray(payload)) {
    state.mensagens = payload
    return
  }
  const seen = {}
  const deduped = []
  for (const item of payload) {
    const id = item.id
    if (id && seen[id]) continue
    if (id) seen[id] = true
    deduped.push(item)
  }
  // Histórico importado tem IDs novos com datas antigas — ordena por data_mensagem.
  sortByDataMensagem(deduped)
  state.mensagens = deduped
}

export const SET_SELECTED_CONVERSA = (state, payload) => {
  state.selectedConversa = payload
}

// Atualiza apenas `state.selectedConversa.contato` preservando a identidade
// de `selectedConversa`. Usar quando refresh de dados do contato (ex:
// participantes de grupo) NÃO deve ser interpretado como troca de conversa
// — watchers de `selectedConversa` (ex.: chat-window: reset de drafts,
// foco do input) não devem disparar nesses casos.
export const SET_SELECTED_CONVERSA_CONTATO = (state, contato) => {
  if (!state.selectedConversa || !contato) return
  state.selectedConversa.contato = contato
}

export const SET_LOADING_CONVERSAS = (state, payload) => {
  state.loadingConversas = payload
}

export const SET_LOADING_MENSAGENS = (state, payload) => {
  state.loadingMensagens = payload
}

export const ADD_MENSAGEM = (state, payload) => {
  if (!payload) return
  const id = payload.id
  if (id) {
    for (const msg of state.mensagens) {
      if (msg.id === id) return
    }
  }
  const list = [].concat(state.mensagens, [payload])
  sortByDataMensagem(list)
  state.mensagens = list
}

export const UPDATE_CONVERSA = (state, payload) => {
  const index = state.conversas.findIndex((c) => c.id === payload.id)
  if (index !== -1) {
    state.conversas.splice(index, 1, { ...state.conversas[index], ...payload })
  }
}

export const SET_UPLOAD_PROGRESS = (state, payload) => {
  state.uploadProgress = payload
}

export const SET_REPLY_TO = (state, payload) => {
  state.replyTo = payload
}

export const SET_NOTE_MODE = (state, payload) => {
  state.noteMode = payload
}

export const SET_EDITING_MESSAGE = (state, payload) => {
  state.editingMessage = payload
}

export const UPDATE_MENSAGEM = (state, payload) => {
  const index = state.mensagens.findIndex(function (m) {
    return m.id === payload.id
  })
  if (index === -1) return
  const previous = state.mensagens[index]
  state.mensagens.splice(index, 1, { ...previous, ...payload })
  // Se ``data_mensagem`` mudou (ex.: poll trazendo ISO completo pra
  // substituir um timestamp truncado de syncs antigos), reordena a
  // lista pra a posição não ficar inconsistente com o novo valor —
  // sem isso a próxima inserção via ADD_MENSAGEM chama o sort e a
  // mensagem "pula" surpreendendo o usuário.
  if (
    payload &&
    'data_mensagem' in payload &&
    payload.data_mensagem !== previous.data_mensagem
  ) {
    sortByDataMensagem(state.mensagens)
  }
}

// Replace an optimistic message (identified by temp_id) with the real server
// response. A optimistic já está no fim da lista (porque _optimistic empurra
// pro fim no sortByDataMensagem), então o splice in-place mantém a posição
// cronológica correta da chegada local. NÃO re-ordenar aqui — o backend grava
// `data_mensagem` de entrada com timestamp do WhatsApp e de saída com
// timestamp do banco, e re-sortar agora poderia mover a saída pra antes de
// uma entrada que chegou cronologicamente antes (clock skew servidor).
export const REPLACE_OPTIMISTIC_MENSAGEM = (state, { temp_id, real }) => {
  if (!temp_id || !real) return
  const index = state.mensagens.findIndex(function (m) {
    return m.id === temp_id
  })
  if (index === -1) return
  const list = state.mensagens.slice()
  list.splice(index, 1, { ...real })
  state.mensagens = list
}

export const REMOVE_MENSAGEM = (state, id) => {
  if (!id) return
  const index = state.mensagens.findIndex(function (m) {
    return m.id === id
  })
  if (index !== -1) {
    state.mensagens.splice(index, 1)
  }
}

// Lookup maps {id→status} e {whatsapp_message_id→status} pra bulk update.
function buildStatusMaps(payload) {
  const byId = {}
  const byWaId = {}
  for (const item of payload) {
    if (!item) continue
    if (item.id != null) byId[item.id] = item.status
    if (item.whatsapp_message_id) byWaId[item.whatsapp_message_id] = item.status
  }
  return { byId, byWaId }
}

// Apply a bulk status update for outgoing messages.
// payload: [{id, whatsapp_message_id, status}]
export const APPLY_DELIVERY_STATUSES = (state, payload) => {
  if (!Array.isArray(payload) || payload.length === 0) return
  const { byId, byWaId } = buildStatusMaps(payload)
  let changed = false
  const list = state.mensagens
  for (let j = 0; j < list.length; j++) {
    const msg = list[j]
    if (msg.direcao !== 'saida') continue
    const newStatus = byId[msg.id] || byWaId[msg.whatsapp_message_id]
    if (newStatus && msg.status !== newStatus) {
      list.splice(j, 1, { ...msg, status: newStatus })
      changed = true
    }
  }
  if (changed) state.mensagens = list.flat()
}

export const PREPEND_MENSAGENS = (state, payload) => {
  if (!Array.isArray(payload) || payload.length === 0) return
  const existingIds = {}
  for (const msg of state.mensagens) {
    existingIds[msg.id] = true
  }
  const newMsgs = []
  for (const msg of payload) {
    if (!existingIds[msg.id]) {
      newMsgs.push(msg)
    }
  }
  const merged = newMsgs.concat(state.mensagens)
  sortByDataMensagem(merged)
  state.mensagens = merged
}

export const SET_RESPOSTAS_RAPIDAS = (state, payload) => {
  state.respostasRapidas = payload
}

export const SET_FILTRO_STATUS = (state, payload) => {
  state.filtroStatus = payload
}

export const SET_FILTRO_PROFISSIONAL = (state, payload) => {
  state.filtroProfissional = payload
}

export const SET_MODE = (state, payload) => {
  state.mode = payload
}

// === Solicitações REAIS (backend aawz_solicitacoes) ===
// Mutations do módulo Solicitações — 100% REAL (backend aawz_solicitacoes).
export const SET_SOLICITACOES = (state, payload) => {
  state.solicitacoes.data = payload || []
  state.solicitacoes.lastUpdate = new Date()
}
export const SET_SOLICITACOES_LOADING = (state, payload) => {
  state.solicitacoes.loading = !!payload
}

export const SET_AREAS = (state, payload) => {
  state.areas.data = payload || []
  state.areas.lastUpdate = new Date()
}
export const SET_AREAS_LOADING = (state, payload) => {
  state.areas.loading = !!payload
}

export const SET_TIPOS = (state, payload) => {
  state.tipos.data = payload || []
  state.tipos.lastUpdate = new Date()
}
export const SET_TIPOS_LOADING = (state, payload) => {
  state.tipos.loading = !!payload
}

// Catálogo BACKOFFICE (esteira interna) — slot ADITIVO: nunca escreve em
// state.tipos/state.areas (catálogo estratégico consumido por outros módulos).
export const SET_CATALOGO_BACKOFFICE_TIPOS = (state, payload) => {
  state.catalogoBackoffice.tipos.data = payload || []
  state.catalogoBackoffice.tipos.lastUpdate = new Date()
}
export const SET_CATALOGO_BACKOFFICE_AREAS = (state, payload) => {
  state.catalogoBackoffice.areas.data = payload || []
  state.catalogoBackoffice.areas.lastUpdate = new Date()
}
export const SET_CATALOGO_BACKOFFICE_LOADING = (state, payload) => {
  state.catalogoBackoffice.tipos.loading = !!payload
  state.catalogoBackoffice.areas.loading = !!payload
}

export const SET_TAREFAS = (state, payload) => {
  state.tarefas.data = payload || []
  state.tarefas.lastUpdate = new Date()
}
export const SET_TAREFAS_LOADING = (state, payload) => {
  state.tarefas.loading = !!payload
}

export const SET_ARQUIVOS = (state, payload) => {
  state.arquivos.data = payload || []
  state.arquivos.lastUpdate = new Date()
}
export const SET_ARQUIVOS_LOADING = (state, payload) => {
  state.arquivos.loading = !!payload
}

// === Configurações (Settings > Solicitações — visão INTERNA AAWZ) ===
// Helper: garante a sub-árvore configuracoes[chave] antes de gravar (defensivo
// contra montagens parciais de state em teste).
function ensureConfigBloco(state, chave) {
  if (!state.configuracoes) state.configuracoes = {}
  if (!state.configuracoes[chave]) {
    state.configuracoes[chave] = { loading: false, lastUpdate: null, data: [] }
  }
  return state.configuracoes[chave]
}

export const SET_CONFIG_TIPOS = (state, payload) => {
  const bloco = ensureConfigBloco(state, 'tipos')
  bloco.data = payload || []
  bloco.lastUpdate = new Date()
}
export const SET_CONFIG_TIPOS_LOADING = (state, payload) => {
  ensureConfigBloco(state, 'tipos').loading = !!payload
}

export const SET_CONFIG_VINCULOS = (state, payload) => {
  const bloco = ensureConfigBloco(state, 'vinculos')
  bloco.data = payload || []
  bloco.lastUpdate = new Date()
}
export const SET_CONFIG_VINCULOS_LOADING = (state, payload) => {
  ensureConfigBloco(state, 'vinculos').loading = !!payload
}

export const SET_CONFIG_CONEXOES = (state, payload) => {
  const bloco = ensureConfigBloco(state, 'conexoes')
  bloco.data = payload || []
  bloco.lastUpdate = new Date()
}
export const SET_CONFIG_CONEXOES_LOADING = (state, payload) => {
  ensureConfigBloco(state, 'conexoes').loading = !!payload
}

export const SET_CONFIG_VINCULOS_ESTAGIO = (state, payload) => {
  const bloco = ensureConfigBloco(state, 'vinculosEstagio')
  bloco.data = payload || []
  bloco.lastUpdate = new Date()
}
export const SET_CONFIG_VINCULOS_ESTAGIO_LOADING = (state, payload) => {
  ensureConfigBloco(state, 'vinculosEstagio').loading = !!payload
}

// Atualização OTIMISTA de um vínculo serviço×etapa: casa por (id_tipo,
// nivel_estagio) e atualiza `obrigatorio` in-place; se o par ainda não existe,
// insere uma nova linha (id temporário). NÃO carimba lastUpdate — é edição
// local de UI. A reversão usa SET_CONFIG_VINCULOS_ESTAGIO com o snapshot.
export const UPSERT_CONFIG_VINCULO_ESTAGIO = (state, payload) => {
  if (!payload || !payload.id_tipo || !payload.nivel_estagio) return
  const bloco = ensureConfigBloco(state, 'vinculosEstagio')
  const lista = bloco.data || []
  const index = lista.findIndex((v) => {
    return (
      v &&
      v.id_tipo === payload.id_tipo &&
      v.nivel_estagio === payload.nivel_estagio
    )
  })
  if (index !== -1) {
    lista.splice(index, 1, {
      ...lista[index],
      obrigatorio: !!payload.obrigatorio,
    })
  } else {
    lista.push({
      id: 'optimistic-' + payload.id_tipo + ':' + payload.nivel_estagio,
      id_tipo: payload.id_tipo,
      nivel_estagio: payload.nivel_estagio,
      obrigatorio: !!payload.obrigatorio,
    })
  }
  bloco.data = lista.slice()
}

// Atualização OTIMISTA de um vínculo: re-aponta (ou insere) o vínculo do tipo
// para a nova conexão ANTES do request. Substitui in-place o item ativo do
// `id_solicitacao_tipo`; se não houver, insere um novo (id temporário). NÃO
// carimba lastUpdate — é edição local de UI, não uma carga do backend. A
// reversão usa SET_CONFIG_VINCULOS com o snapshot anterior.
export const UPSERT_CONFIG_VINCULO = (state, payload) => {
  if (!payload || !payload.id_solicitacao_tipo) return
  const bloco = ensureConfigBloco(state, 'vinculos')
  const lista = bloco.data || []
  const index = lista.findIndex((v) => {
    return (
      v &&
      v.ativo !== false &&
      v.id_solicitacao_tipo === payload.id_solicitacao_tipo
    )
  })
  if (index !== -1) {
    lista.splice(index, 1, { ...lista[index], ...payload, ativo: true })
  } else {
    lista.push({
      id: payload.id || 'optimistic-' + payload.id_solicitacao_tipo,
      id_solicitacao_tipo: payload.id_solicitacao_tipo,
      id_conexao: payload.id_conexao,
      ativo: true,
    })
  }
  bloco.data = lista.slice()
}

export const SET_SESSAO = (state, payload) => {
  state.sessao = payload || null
}

// Pergunta pendente do Consultor (spec-008). Payload vazio limpa — o state guarda sempre
// String, nunca undefined, para o getter poder virar prop direto.
export const SET_PERGUNTA_PENDENTE_CONSULTOR = (state, payload) => {
  state.perguntaPendenteConsultor = payload || ''
}

export const SET_CONSULTOR_PAINEL_ABERTO = (state, payload) => {
  state.consultorPainelAberto = !!payload
}

export const SET_LOADING = (state, payload) => {
  state.loading = !!payload
}

export const SET_LOADED = (state, payload) => {
  state.loaded = !!payload
}

// --- OPERACAO_AAWZ (AAWZ profissional, cross-office). Mesmo padrão block dos reais; lastUpdate
// marca o "carregado". Carga via apiSolicitacoesOperacaoAawz.
export const SET_OPERACAO_AAWZ_SOLICITACOES = (state, payload) => {
  state.operacaoAawzSolicitacoes.data = Array.isArray(payload) ? payload : []
  state.operacaoAawzSolicitacoes.lastUpdate = new Date()
}

export const SET_OPERACAO_AAWZ_SOLICITACOES_LOADING = (state, payload) => {
  state.operacaoAawzSolicitacoes.loading = !!payload
}

// Cache de árvores por solicitação (merge de { [id]: arvore[] }; novo objeto p/ reatividade Vue 2).
// Alimentado pela fila p/ derivar prazos na tabela. Ignora entradas sem id ou sem array.
export const SET_OPERACAO_AAWZ_ARVORES = (state, payload) => {
  const mapa = payload || {}
  const novo = { ...state.operacaoAawzArvores }
  for (const id of Object.keys(mapa)) {
    if (id && Array.isArray(mapa[id])) novo[id] = mapa[id]
  }
  state.operacaoAawzArvores = novo
}

// Substitui as tarefas de solicitação da aba (lista) + o contexto do board (folder/sprint).
export const SET_OPERACAO_AAWZ_BOARD_TASKS = (state, payload) => {
  const p = payload || {}
  state.operacaoAawzBoardTasks = {
    ...state.operacaoAawzBoardTasks,
    data: Array.isArray(p.data) ? p.data : [],
    folderId: p.folderId || null,
    sprintId: p.sprintId || null,
    lastUpdate: new Date(),
  }
}

export const SET_OPERACAO_AAWZ_BOARD_TASKS_LOADING = (state, payload) => {
  state.operacaoAawzBoardTasks = { ...state.operacaoAawzBoardTasks, loading: !!payload }
}

// DETALHE interno — commits granulares (render progressivo): solicitação primeiro, depois
// árvore + arquivos. A solicitação carimba o lastUpdate (a página sai do LOADING).
export const SET_OPERACAO_AAWZ_DETALHE_SOLICITACAO = (state, payload) => {
  state.operacaoAawzDetalhe.data.solicitacao = payload || null
  state.operacaoAawzDetalhe.lastUpdate = new Date()
}

export const SET_OPERACAO_AAWZ_DETALHE_ARVORE = (state, payload) => {
  state.operacaoAawzDetalhe.data.arvore = Array.isArray(payload) ? payload : []
}

export const SET_OPERACAO_AAWZ_DETALHE_ARQUIVOS = (state, payload) => {
  state.operacaoAawzDetalhe.data.arquivos = Array.isArray(payload) ? payload : []
}

export const SET_OPERACAO_AAWZ_DETALHE_LOADING = (state, payload) => {
  state.operacaoAawzDetalhe.loading = !!payload
}

export const SET_OPERACAO_AAWZ_DETALHE_ERRO = (state, payload) => {
  state.operacaoAawzDetalhe.erro = !!payload
}

export const SET_OPERACAO_AAWZ_DETALHE_ARVORE_CARREGANDO = (state, payload) => {
  state.operacaoAawzDetalhe.arvoreCarregando = !!payload
}

// PATCH OTIMISTA do status da SOLICITAÇÃO no detalhe interno (recria o objeto p/ reatividade).
// Reflete a pílula de status sem reload. Ignora se não há solicitação carregada ou status vazio.
export const PATCH_OPERACAO_AAWZ_DETALHE_STATUS = (state, payload) => {
  const p = payload || {}
  const sol = state.operacaoAawzDetalhe.data.solicitacao
  if (!sol || !p.status) return
  // Guarda de identidade FAIL-CLOSED: o patch só vale para a solicitação que
  // ESTÁ aberta, e payload SEM `id` não passa. Sem a guarda, um caller que
  // atualize o status de outra solicitação (o convite de conclusão, disparado
  // do quadro de Tarefas) reescrevia o status desta — e o cache do detalhe
  // impede o refetch que corrigiria. Aceitar payload sem `id` deixaria a porta
  // aberta para o próximo caller que esquecer de mandá-lo; o único caller de
  // produção sempre manda (`apiSolicitacoesOperacaoAawz.atualizarStatus`), e
  // não patchear é a degradação segura — a pílula fica desatualizada até o
  // próximo fetch, em vez de mostrar o status de OUTRA solicitação.
  if (!p.id || String(sol.id) !== String(p.id)) return
  state.operacaoAawzDetalhe.data.solicitacao = { ...sol, status: p.status }
}

// PATCH OTIMISTA do status de UMA etapa (pm_task) na árvore do detalhe interno. Recria o array
// (map) para a reatividade do Vue 2 disparar — o stepper reflete a mudança sem reload (sem
// piscar skeleton nem perder o scroll). Ignora payload sem id.
export const PATCH_OPERACAO_AAWZ_DETALHE_ETAPA = (state, payload) => {
  const p = payload || {}
  if (!p.id) return
  const arvore = (state.operacaoAawzDetalhe.data.arvore || []).map((no) =>
    no && no.id === p.id ? { ...no, status: p.status } : no
  )
  state.operacaoAawzDetalhe.data.arvore = arvore
}
